<script setup>
// U3: the compatibility report for one automation rule, then - only on
// explicit confirmation - one DRAFT flow. The rule itself is never touched.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ConversionsAPI from 'dashboard/api/teletel/automationConversions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  rule: { type: Object, default: null },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const router = useRouter();

const isLoading = ref(false);
const isConverting = ref(false);
const report = ref(null);
const hasError = ref(false);

const statusIcon = item => {
  if (!item.supported) return '✗';
  return item.approximate ? '≈' : '✓';
};
const statusClass = item => {
  if (!item.supported) return 'text-n-ruby-11';
  return item.approximate ? 'text-n-amber-11' : 'text-n-teal-11';
};

watch(
  () => props.rule,
  async rule => {
    report.value = null;
    hasError.value = false;
    if (!rule) return;
    isLoading.value = true;
    try {
      const { data } = await ConversionsAPI.preview(rule.id);
      report.value = data;
    } catch {
      hasError.value = true;
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

const canConvert = computed(() => report.value?.convertible);

const convert = async () => {
  isConverting.value = true;
  try {
    const { data } = await ConversionsAPI.convert(props.rule.id);
    useAlert(t('AUTOMATION_FLOWS_GATEWAY.CONVERT.DONE'));
    emit('close');
    router.push({
      name: 'teletel_flows_edit',
      params: { flowId: data.flow_id },
    });
  } catch {
    useAlert(t('AUTOMATION_FLOWS_GATEWAY.CONVERT.ERROR'));
  } finally {
    isConverting.value = false;
  }
};
</script>

<template>
  <div
    v-if="rule"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-lg rounded-xl bg-n-solid-1 border border-n-weak p-5 max-h-[85vh] overflow-auto"
    >
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.SAFETY') }}
      </p>

      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner />
      </div>

      <p v-else-if="hasError" class="py-6 text-sm text-n-ruby-11">
        {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.ERROR') }}
      </p>

      <template v-else-if="report">
        <div class="mt-4 flex flex-col gap-3 text-sm">
          <div>
            <p class="font-medium text-n-slate-12">
              {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.TRIGGER') }}
            </p>
            <p :class="statusClass(report.trigger)">
              {{ statusIcon(report.trigger) }} {{ report.trigger.event }}
              <span v-if="report.trigger.supported">
                {{ `← ${report.trigger.trigger}` }}</span
              >
            </p>
          </div>

          <div v-if="report.conditions.length">
            <p class="font-medium text-n-slate-12">
              {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.CONDITIONS') }}
            </p>
            <p
              v-for="(condition, index) in report.conditions"
              :key="index"
              :class="statusClass(condition)"
            >
              {{ statusIcon(condition) }} {{ condition.attribute_key }}
              <span v-if="condition.supported">{{
                `← ${condition.maps_to}`
              }}</span>
            </p>
          </div>

          <div>
            <p class="font-medium text-n-slate-12">
              {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.ACTIONS') }}
            </p>
            <p
              v-for="(action, index) in report.actions"
              :key="index"
              :class="statusClass(action)"
            >
              {{ statusIcon(action) }} {{ action.action_name }}
              <span v-if="action.supported">{{ `← ${action.maps_to}` }}</span>
              <span v-if="action.external" class="text-n-amber-11">
                ({{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.EXTERNAL') }})</span
              >
            </p>
          </div>

          <p class="text-xs text-n-slate-11">
            {{ t('AUTOMATION_FLOWS_GATEWAY.CONVERT.LEGEND') }}
          </p>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <NextButton
            faded
            slate
            :label="t('AUTOMATION_FLOWS_GATEWAY.CONVERT.CANCEL')"
            @click="emit('close')"
          />
          <NextButton
            :label="t('AUTOMATION_FLOWS_GATEWAY.CONVERT.CONFIRM')"
            :disabled="!canConvert"
            :is-loading="isConverting"
            @click="convert"
          />
        </div>
      </template>
    </div>
  </div>
</template>
